import React, { useEffect, useState } from "react";
// ANT DESIGN
import { Card, Tooltip, Button, Typography, Switch, Input, InputNumber } from "antd";
// FONT AWESOME
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb, faPlus, faSave, faTrash, faWarning } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import usePutAdapter from "../../../../customHooks/PUT_requests/usePutAdapter";
// INTERFACES
import { RootState } from "../../../../app/store";
import { FaultConfigurationInterface } from "../../../../interfaces/Monitoring.interface";
import { AdapterInterface } from "../../../../interfaces/Adapter.interface";
// CONSTANTS
import { DEFAULT_FAULTS_CONFIGURATION } from "../../../../constants/monitoring";

export default function FaultConfiguration({
	isLoadingGetOneBank,
	adapter,
}: {
	isLoadingGetOneBank: boolean;
	adapter: AdapterInterface;
}) {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const { Text } = Typography;

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [customConfiguration, setCustomConfiguration] = useState<boolean>(false);
	const [faultConfiguration, setFaultConfiguration] = useState<FaultConfigurationInterface>({
		...DEFAULT_FAULTS_CONFIGURATION,
	});
	const [rule, setRule] = useState<string>("");
	const [ruleCounter, setRuleCounter] = useState<number>(0);

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */
	const { putAdapter, isErrorPutAdapter, isSuccessPutAdapter, isLoadingPutAdapter } = usePutAdapter();

	// ************************************************ */
	// HELPERS **************************************** */

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */

	// ************************************************ */
	// USE MEMO *************************************** */

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onClickSave = () => {
		putAdapter({
			...adapter,
			leading_zeroes_id: adapter.leading_zeroes_id ?? "",
			default_faults_configuration: !customConfiguration,
			faults_configuration: faultConfiguration,
		});
	};

	const disableButton = (): boolean =>
		faultConfiguration.rules.some((item: { name: string; counter: number }) => item.name === rule);

	// ************************************************ */
	// USE CALLBACK *********************************** */

	// ************************************************ */
	// USE EFFECT ************************************* */
	useEffect(() => {
		if (isErrorPutAdapter || isSuccessPutAdapter) {
			setCustomConfiguration(false);
			setFaultConfiguration({ ...DEFAULT_FAULTS_CONFIGURATION });
			setRule("");
			setRuleCounter(0);
		}
	}, [isErrorPutAdapter, isSuccessPutAdapter]);

	useEffect(() => {
		if (adapter && adapter.id) {
			setCustomConfiguration(!adapter.default_faults_configuration);
			setFaultConfiguration(adapter?.faults_configuration);
		}
	}, [adapter]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Card
			loading={isLoadingGetOneBank || isLoadingPutAdapter}
			className='bankFloorsFriendly'
			size='small'
			title={
				<div className='generalStyles__spaceBetween'>
					<div style={{ paddingTop: "8px", marginBottom: "5px" }}>
						<FontAwesomeIcon className='generalStyles__mrFix generalStyles__info' icon={faWarning} />
						<span>Faults</span>
					</div>
					<div>
						<Tooltip title='save'>
							<Button onClick={onClickSave} type='primary' icon={<FontAwesomeIcon icon={faSave} />} />
						</Tooltip>
					</div>
				</div>
			}
			bordered
		>
			{/** ************************************* */}
			{/** DEFAULT CONFIGURATION */}
			<div style={{ display: "flex", gap: 16, alignItems: "stretch" }}>
				<div
					style={{
						width: 10,
						borderRadius: 999,
						background: "rgba(0,0,0,0.08)",
					}}
				/>
				<div
					style={{ padding: "10px", borderRadius: "7px" }}
					className={`generalStyles__box__${theme} generalStyles__spaceBetween generalStyles__width100`}
				>
					<div>Custom configuration</div>
					<div>
						<Switch
							style={{ marginRight: "10px", marginLeft: "25px" }}
							checkedChildren='Custom'
							unCheckedChildren='Default'
							checked={customConfiguration}
							onChange={(e) => {
								setCustomConfiguration(e);
							}}
						/>
					</div>
				</div>
			</div>
			{/** ************************************* */}
			{/** SHOW COUNTER */}
			<div style={{ display: "flex", gap: 16, alignItems: "stretch", marginTop: "10px" }}>
				<div
					style={{
						width: 10,
						borderRadius: 999,
						background: "rgba(0,0,0,0.08)",
					}}
				/>
				<div
					style={{ padding: "10px", borderRadius: "7px" }}
					className={`generalStyles__box__${theme} generalStyles__spaceBetween generalStyles__width100`}
				>
					<div>Show counter</div>
					<div>
						<Switch
							style={{ marginRight: "10px", marginLeft: "25px" }}
							checkedChildren='Counter'
							unCheckedChildren='Counter'
							checked={faultConfiguration.showCounter}
							onChange={(e) => {
								setFaultConfiguration((prev: FaultConfigurationInterface) => ({
									rules: prev.rules,
									showCounter: e,
								}));
							}}
							disabled={!customConfiguration}
						/>
					</div>
				</div>
			</div>

			{/** ************************************* */}
			{/** ADD NEW RULE */}
			<div style={{ display: "flex", gap: 16, alignItems: "stretch", marginTop: "10px" }}>
				<div
					style={{
						width: 10,
						borderRadius: 999,
						background: "rgba(0,0,0,0.08)",
					}}
				/>

				<div
					style={{ padding: "10px", borderRadius: "7px" }}
					className={`generalStyles__box__${theme} generalStyles__spaceBetween generalStyles__width100`}
				>
					<div>Delay seconds</div>
					<div className='generalStyles__flex'>
						<div>
							<InputNumber
								className='generalStyles__input'
								style={{ width: "100%" }}
								min={0}
								value={ruleCounter}
								onChange={(e: any) => {
									setRuleCounter(e);
								}}
								disabled={!customConfiguration}
							/>{" "}
						</div>
					</div>
				</div>
				<div
					style={{ padding: "10px", borderRadius: "7px" }}
					className={`generalStyles__box__${theme} generalStyles__spaceBetween generalStyles__width100`}
				>
					<div>Add new rule</div>
					<div className='generalStyles__flex'>
						<div>
							<Input
								disabled={!customConfiguration}
								className='generalStyles__width100'
								onChange={(e) => setRule(e.target.value)}
								value={rule}
							/>
						</div>
						<div>
							<Tooltip title='save'>
								<Button
									onClick={() => {
										setRule("");
										setFaultConfiguration((prev: FaultConfigurationInterface) => ({
											showCounter: prev.showCounter,
											rules: [...prev.rules, { name: rule, counter: ruleCounter }],
										}));
									}}
									disabled={disableButton() || !customConfiguration}
									type='primary'
									icon={<FontAwesomeIcon icon={faPlus} />}
								/>
							</Tooltip>
						</div>
					</div>
				</div>
			</div>
			{/** ************************************* */}
			{/** RULES */}
			<div style={{ display: "flex", gap: 16, alignItems: "stretch", marginTop: "10px" }}>
				<div
					style={{
						width: 10,
						borderRadius: 999,
						background: "rgba(0,0,0,0.08)",
					}}
				/>
				<div
					style={{ padding: "10px", borderRadius: "7px" }}
					className={`generalStyles__box__${theme} generalStyles__spaceBetween generalStyles__width100`}
				>
					<div>Rules</div>
					<div>
						{faultConfiguration.rules.map((item: { name: string; counter: number }) => (
							<div className='generalStyles__flex mt-2'>
								<div className='generalStyles__flex'>
									<InputNumber
										className='generalStyles__input'
										style={{ marginRight: "10px" }}
										value={item.counter}
										disabled
									/>
									<Input className='generalStyles__width100' value={item.name} disabled />
								</div>
								<div>
									<Tooltip title='delete'>
										<Button
											onClick={() => {
												setFaultConfiguration((prev: FaultConfigurationInterface) => ({
													showCounter: prev.showCounter,
													rules: prev.rules.filter((r: { name: string; counter: number }) => r.name !== item.name),
												}));
											}}
											icon={<FontAwesomeIcon icon={faTrash} />}
										/>
									</Tooltip>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
			<div style={{ marginTop: 10 }}>
				<FontAwesomeIcon className='generalStyles__mrFix generalStyles__warning' icon={faLightbulb} />
				<Text type='secondary'>Note: This configuration is applied to all lifts assigned to the selected adapter.</Text>
			</div>
		</Card>
	);
}
